import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LogOut,
  RefreshCw,
  CalendarX,
  Search,
  Calendar as CalendarIcon,
  Phone,
  Mail,
  MapPin,
  User,
  Shield,
  BadgeCheck,
  XCircle,
  Inbox,
  BarChart3,
  Heart,
  AlertTriangle,
  Link2,
  Trash2,
  Send,
  Settings as SettingsIcon,
  CheckCircle2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const statusLabels = {
  confirmed: { label: 'Bestätigt', color: 'bg-[#40E0D0]/20 text-[#0a8377] border-[#40E0D0]/40' },
  cancelled: { label: 'Storniert', color: 'bg-red-50 text-red-600 border-red-200' },
};

const formatDate = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  return d.toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: 'long', year: 'numeric' });
};

const formatDateTime = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  return d.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const formatBirthDate = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleDateString('de-DE');
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [calendarStatus, setCalendarStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('confirmed');
  const [selected, setSelected] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [telegramSettings, setTelegramSettings] = useState(null);
  const [telegramForm, setTelegramForm] = useState({ bot_token: '', chat_id: '' });
  const [telegramSaving, setTelegramSaving] = useState(false);
  const [telegramTesting, setTelegramTesting] = useState(false);

  const token = localStorage.getItem('admin_token');
  const authHeader = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    if (!token) {
      navigate('/admin');
      return;
    }
    loadBookings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAuthError = (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('admin_token');
      navigate('/admin');
      toast.error('Sitzung abgelaufen, bitte erneut anmelden');
      return true;
    }
    return false;
  };

  const loadBookings = async () => {
    setLoading(true);
    try {
      const [bRes, sRes, cRes] = await Promise.all([
        axios.get(`${API}/api/admin/bookings`, { headers: authHeader }),
        axios.get(`${API}/api/admin/stats`, { headers: authHeader }),
        axios.get(`${API}/api/oauth/calendar/status`),
      ]);
      setBookings(bRes.data.bookings || []);
      setStats(sRes.data);
      setCalendarStatus(cRes.data);
    } catch (err) {
      if (!handleAuthError(err)) toast.error('Buchungen konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const { data } = await axios.get(`${API}/api/admin/sync-calendar`, { headers: authHeader });
      toast.success(data.message || 'Kalender synchronisiert');
      loadBookings();
    } catch (err) {
      if (!handleAuthError(err)) toast.error('Synchronisation fehlgeschlagen');
    } finally {
      setSyncing(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin');
  };

  const handleCancel = async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    try {
      await axios.post(
        `${API}/api/admin/bookings/${cancelTarget.id}/cancel`,
        {},
        { headers: authHeader }
      );
      toast.success('Termin storniert');
      setCancelTarget(null);
      setSelected(null);
      loadBookings();
    } catch (err) {
      if (!handleAuthError(err)) toast.error('Stornierung fehlgeschlagen');
    } finally {
      setCancelling(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(
        `${API}/api/admin/bookings/${deleteTarget.id}`,
        { headers: authHeader }
      );
      toast.success('Termin endgültig gelöscht');
      setDeleteTarget(null);
      setSelected(null);
      loadBookings();
    } catch (err) {
      if (!handleAuthError(err)) {
        const msg = err.response?.data?.detail || 'Löschen fehlgeschlagen';
        toast.error(typeof msg === 'string' ? msg : 'Löschen fehlgeschlagen');
      }
    } finally {
      setDeleting(false);
    }
  };

  const openSettings = async () => {
    setSettingsOpen(true);
    try {
      const { data } = await axios.get(`${API}/api/admin/settings/telegram`, { headers: authHeader });
      setTelegramSettings(data);
      setTelegramForm({ bot_token: '', chat_id: data.chat_id || '' });
    } catch (err) {
      if (!handleAuthError(err)) toast.error('Einstellungen konnten nicht geladen werden');
    }
  };

  const updateTelegram = async (patch) => {
    setTelegramSaving(true);
    try {
      const { data } = await axios.put(`${API}/api/admin/settings/telegram`, patch, { headers: authHeader });
      setTelegramSettings(data);
      setTelegramForm((f) => ({ ...f, bot_token: '' }));
      toast.success('Gespeichert');
    } catch (err) {
      if (!handleAuthError(err)) toast.error('Speichern fehlgeschlagen');
    } finally {
      setTelegramSaving(false);
    }
  };

  const handleTelegramTest = async () => {
    setTelegramTesting(true);
    try {
      await axios.post(`${API}/api/admin/settings/telegram/test`, {}, { headers: authHeader });
      toast.success('Testnachricht gesendet! Schauen Sie in Ihren Telegram-Chat.');
    } catch (err) {
      if (!handleAuthError(err)) {
        const msg = err.response?.data?.detail || 'Test fehlgeschlagen';
        toast.error(typeof msg === 'string' ? msg : 'Test fehlgeschlagen');
      }
    } finally {
      setTelegramTesting(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return bookings.filter((b) => {
      if (statusFilter !== 'all' && b.status !== statusFilter) return false;
      if (!q) return true;
      return (
        `${b.first_name} ${b.last_name}`.toLowerCase().includes(q) ||
        (b.email || '').toLowerCase().includes(q) ||
        (b.phone || '').toLowerCase().includes(q) ||
        (b.appointment_date || '').includes(q)
      );
    });
  }, [bookings, search, statusFilter]);

  const summary = useMemo(() => {
    const today = new Date().toISOString().split('T')[0];
    return {
      total: bookings.length,
      confirmed: bookings.filter((b) => b.status === 'confirmed').length,
      cancelled: bookings.filter((b) => b.status === 'cancelled').length,
      upcoming: bookings.filter((b) => b.status === 'confirmed' && b.appointment_date >= today).length,
    };
  }, [bookings]);

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#E0F7FA] rounded-xl flex items-center justify-center">
              <Shield className="w-5 h-5 text-[#40E0D0]" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Merriweather, serif' }}>
                Admin-Dashboard
              </h1>
              <p className="text-xs text-slate-500">Pflegeberatung Buchungen</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={openSettings}
              data-testid="admin-settings-btn"
            >
              <SettingsIcon className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Einstellungen</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleSync}
              disabled={syncing}
              data-testid="admin-sync-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Kalender-Sync</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-slate-600"
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Abmelden</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Calendar connection warning */}
        {calendarStatus && !calendarStatus.connected && (
          <div
            className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-6 flex flex-col sm:flex-row sm:items-center gap-3"
            data-testid="calendar-warning"
          >
            <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center shrink-0">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
            </div>
            <div className="flex-1">
              <div className="font-semibold text-amber-900">
                Google Kalender nicht verbunden
              </div>
              <div className="text-sm text-amber-800">
                {calendarStatus.reason === 'token_invalid'
                  ? 'Der Zugriff ist abgelaufen (bei Google-Cloud-Apps im „Test"-Modus nach 7 Tagen üblich). Bitte Kalender neu verbinden, damit neue Termine wieder eingetragen werden.'
                  : 'Bitte verbinden Sie Ihren Google Kalender, damit Termine automatisch eingetragen werden.'}
              </div>
            </div>
            <a
              href={`${API}/api/oauth/calendar/login`}
              className="shrink-0 inline-flex items-center gap-2 px-4 py-2 bg-[#40E0D0] hover:bg-[#36c4b6] text-slate-900 font-semibold rounded-xl text-sm"
              data-testid="calendar-reconnect-btn"
            >
              <Link2 className="w-4 h-4" />
              Kalender verbinden
            </a>
          </div>
        )}
        {calendarStatus && calendarStatus.connected && (
          <div className="mb-6 text-xs text-slate-500 flex items-center gap-2" data-testid="calendar-ok">
            <span className="w-2 h-2 bg-emerald-500 rounded-full"></span>
            Google Kalender verbunden{calendarStatus.email ? ` (${calendarStatus.email})` : ''}
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="admin-stats">
          <StatCard label="Gesamt" value={summary.total} icon={Inbox} />
          <StatCard label="Bestätigt" value={summary.confirmed} icon={BadgeCheck} accent />
          <StatCard label="Anstehend" value={summary.upcoming} icon={CalendarIcon} />
          <StatCard label="Storniert" value={summary.cancelled} icon={XCircle} muted />
        </div>

        {/* Customer Breakdown */}
        {stats && (stats.by_insurance?.length > 0 || stats.by_care_level?.length > 0) && (
          <div className="grid md:grid-cols-2 gap-4 mb-8" data-testid="admin-breakdown">
            <BreakdownCard
              title="Nach Krankenkasse"
              icon={Heart}
              items={stats.by_insurance}
              keyName="name"
              total={stats.total_confirmed}
            />
            <BreakdownCard
              title="Nach Pflegegrad"
              icon={BarChart3}
              items={stats.by_care_level}
              keyName="level"
              total={stats.total_confirmed}
            />
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-2xl border border-slate-100 p-4 mb-4 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Name, E-Mail, Telefon oder Datum suchen…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="admin-search-input"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="sm:w-56" data-testid="admin-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Status</SelectItem>
              <SelectItem value="confirmed">Bestätigt</SelectItem>
              <SelectItem value="cancelled">Storniert</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Bookings list */}
        <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden" data-testid="admin-bookings-list">
          {loading ? (
            <div className="p-10 text-center text-slate-500">Lädt…</div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center text-slate-500">
              <Inbox className="w-10 h-10 mx-auto text-slate-300 mb-3" />
              Keine Buchungen gefunden
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {filtered.map((b) => {
                const status = statusLabels[b.status] || statusLabels.confirmed;
                return (
                  <div
                    key={b.id}
                    onClick={() => setSelected(b)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setSelected(b)}
                    className="w-full text-left p-4 md:p-5 hover:bg-slate-50 transition-colors flex flex-col md:flex-row md:items-center gap-3 md:gap-6 cursor-pointer"
                    data-testid={`admin-booking-row-${b.id}`}
                  >
                    <div className="flex items-center gap-3 md:w-60">
                      <div className="w-10 h-10 bg-[#E0F7FA] rounded-full flex items-center justify-center shrink-0">
                        <User className="w-5 h-5 text-[#40E0D0]" />
                      </div>
                      <div className="min-w-0">
                        <div className="font-semibold text-slate-900 truncate">
                          {b.first_name} {b.last_name}
                        </div>
                        <div className="text-xs text-slate-500 truncate">{b.email}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-700 md:w-64">
                      <CalendarIcon className="w-4 h-4 text-slate-400 shrink-0" />
                      <span>
                        {formatDate(b.appointment_date)} · <strong>{b.appointment_time}</strong>
                      </span>
                    </div>
                    <div className="hidden md:flex items-center gap-2 text-sm text-slate-600 md:w-40">
                      <Phone className="w-4 h-4 text-slate-400" />
                      <span className="truncate">{b.phone}</span>
                    </div>
                    <div className="md:ml-auto flex items-center gap-2">
                      <span className="text-xs text-slate-500 hidden md:inline">
                        {b.care_level}
                      </span>
                      <Badge className={`${status.color} border`}>{status.label}</Badge>
                      {b.status === 'cancelled' && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteTarget(b);
                          }}
                          data-testid={`admin-delete-row-${b.id}`}
                          title="Endgültig löschen"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="admin-booking-detail">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#E0F7FA] rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-[#40E0D0]" />
                  </div>
                  <div>
                    <div>{selected.first_name} {selected.last_name}</div>
                    <Badge className={`${(statusLabels[selected.status] || statusLabels.confirmed).color} border mt-1`}>
                      {(statusLabels[selected.status] || statusLabels.confirmed).label}
                    </Badge>
                  </div>
                </DialogTitle>
                <DialogDescription>
                  Gebucht am {formatDateTime(selected.created_at)}
                </DialogDescription>
              </DialogHeader>

              <div className="grid sm:grid-cols-2 gap-4 py-4">
                <InfoBox icon={CalendarIcon} label="Termin">
                  {formatDate(selected.appointment_date)}
                  <br />
                  <strong>{selected.appointment_time} Uhr</strong>
                </InfoBox>
                <InfoBox icon={Phone} label="Telefon">{selected.phone}</InfoBox>
                <InfoBox icon={Mail} label="E-Mail">{selected.email}</InfoBox>
                <InfoBox icon={MapPin} label="Adresse">
                  {selected.street}
                  <br />
                  {selected.zip_code} {selected.city}
                </InfoBox>
                <InfoBox icon={User} label="Geburtsdatum">{formatBirthDate(selected.birth_date)}</InfoBox>
                <InfoBox icon={BadgeCheck} label="Pflegegrad">{selected.care_level}</InfoBox>
                <InfoBox icon={Shield} label="Krankenkasse">
                  {selected.insurance_name}
                  <br />
                  <span className="text-xs text-slate-500">Vers.-Nr.: {selected.insurance_number}</span>
                </InfoBox>
                <InfoBox icon={CalendarIcon} label="Kalender-Event">
                  {selected.google_calendar_event_id ? (
                    <span className="text-xs font-mono break-all">{selected.google_calendar_event_id}</span>
                  ) : (
                    <span className="text-slate-400 text-sm">Nicht verknüpft</span>
                  )}
                </InfoBox>
              </div>

              <DialogFooter>
                {selected.status === 'confirmed' && (
                  <Button
                    variant="destructive"
                    onClick={() => setCancelTarget(selected)}
                    data-testid="admin-cancel-booking-btn"
                  >
                    <CalendarX className="w-4 h-4 mr-2" />
                    Termin stornieren
                  </Button>
                )}
                {selected.status === 'cancelled' && (
                  <Button
                    variant="destructive"
                    onClick={() => setDeleteTarget(selected)}
                    data-testid="admin-delete-booking-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Endgültig löschen
                  </Button>
                )}
                <Button variant="outline" onClick={() => setSelected(null)}>Schließen</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Cancel confirmation */}
      <Dialog open={!!cancelTarget} onOpenChange={(o) => !o && setCancelTarget(null)}>
        <DialogContent data-testid="admin-cancel-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Termin wirklich stornieren?</DialogTitle>
            <DialogDescription>
              {cancelTarget && (
                <>
                  Der Termin am <strong>{formatDate(cancelTarget.appointment_date)}</strong> um{' '}
                  <strong>{cancelTarget.appointment_time}</strong> für{' '}
                  <strong>{cancelTarget.first_name} {cancelTarget.last_name}</strong> wird storniert.
                  Der Google-Kalender-Eintrag wird gelöscht und eine Absage an den Patienten gesendet.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelTarget(null)}>Abbrechen</Button>
            <Button
              variant="destructive"
              onClick={handleCancel}
              disabled={cancelling}
              data-testid="admin-confirm-cancel-btn"
            >
              {cancelling ? 'Storniert…' : 'Ja, stornieren'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Permanent delete confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent data-testid="admin-delete-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Termin endgültig löschen?</DialogTitle>
            <DialogDescription>
              {deleteTarget && (
                <>
                  Der stornierte Termin am <strong>{formatDate(deleteTarget.appointment_date)}</strong> um{' '}
                  <strong>{deleteTarget.appointment_time}</strong> für{' '}
                  <strong>{deleteTarget.first_name} {deleteTarget.last_name}</strong> wird unwiderruflich aus der Datenbank entfernt.
                  <br /><br />
                  Diese Aktion kann nicht rückgängig gemacht werden.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Abbrechen</Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleting}
              data-testid="admin-confirm-delete-btn"
            >
              {deleting ? 'Lösche…' : 'Ja, endgültig löschen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settings Dialog (Telegram) */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="max-w-lg" data-testid="admin-settings-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-[#40E0D0]" />
              Telegram-Benachrichtigungen
            </DialogTitle>
            <DialogDescription>
              Erhalten Sie eine Push-Nachricht in Telegram, sobald eine neue Buchung eingeht oder ein Termin storniert wird.
            </DialogDescription>
          </DialogHeader>

          {!telegramSettings ? (
            <div className="py-6 text-center text-slate-500 text-sm">Lädt…</div>
          ) : (
            <div className="space-y-4 py-2">
              <div className="flex items-center justify-between bg-slate-50 rounded-xl p-3">
                <div>
                  <div className="font-semibold text-slate-900">Benachrichtigungen aktiv</div>
                  <div className="text-xs text-slate-500">
                    {telegramSettings.enabled ? (
                      <span className="text-emerald-600 inline-flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" /> Aktiviert
                      </span>
                    ) : (
                      'Deaktiviert'
                    )}
                  </div>
                </div>
                <Switch
                  checked={telegramSettings.enabled}
                  onCheckedChange={(v) => updateTelegram({ enabled: v })}
                  disabled={telegramSaving || !telegramSettings.has_bot_token}
                  data-testid="telegram-enabled-switch"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-sm">Bot-Token</Label>
                <Input
                  type="password"
                  value={telegramForm.bot_token}
                  onChange={(e) => setTelegramForm({ ...telegramForm, bot_token: e.target.value })}
                  placeholder={telegramSettings.has_bot_token ? telegramSettings.bot_token_masked : 'z.B. 1234567890:AAH...'}
                  data-testid="telegram-token-input"
                />
                <p className="text-xs text-slate-500">
                  {telegramSettings.has_bot_token
                    ? 'Lassen Sie das Feld leer, um den aktuellen Token zu behalten.'
                    : 'Token von @BotFather auf Telegram (/newbot oder /mybots).'}
                </p>
              </div>

              <div className="space-y-2">
                <Label className="text-sm">Chat-ID</Label>
                <Input
                  type="text"
                  value={telegramForm.chat_id}
                  onChange={(e) => setTelegramForm({ ...telegramForm, chat_id: e.target.value })}
                  placeholder="z.B. 123456789"
                  data-testid="telegram-chatid-input"
                />
                <p className="text-xs text-slate-500">Ihre persönliche Chat-ID (von @userinfobot abfragen).</p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <label className="flex items-center justify-between bg-slate-50 rounded-xl p-3 cursor-pointer">
                  <span className="text-sm">Bei neuer Buchung</span>
                  <Switch
                    checked={telegramSettings.notify_on_create}
                    onCheckedChange={(v) => updateTelegram({ notify_on_create: v })}
                    disabled={telegramSaving}
                    data-testid="telegram-notify-create-switch"
                  />
                </label>
                <label className="flex items-center justify-between bg-slate-50 rounded-xl p-3 cursor-pointer">
                  <span className="text-sm">Bei Stornierung</span>
                  <Switch
                    checked={telegramSettings.notify_on_cancel}
                    onCheckedChange={(v) => updateTelegram({ notify_on_cancel: v })}
                    disabled={telegramSaving}
                    data-testid="telegram-notify-cancel-switch"
                  />
                </label>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 flex-col sm:flex-row">
            <Button
              variant="outline"
              onClick={handleTelegramTest}
              disabled={telegramTesting || !telegramSettings?.has_bot_token}
              data-testid="telegram-test-btn"
            >
              <Send className="w-4 h-4 mr-2" />
              {telegramTesting ? 'Sende…' : 'Testnachricht senden'}
            </Button>
            <Button
              onClick={() => updateTelegram({
                bot_token: telegramForm.bot_token || undefined,
                chat_id: telegramForm.chat_id || undefined,
              })}
              disabled={telegramSaving || (!telegramForm.bot_token && telegramForm.chat_id === (telegramSettings?.chat_id || ''))}
              className="bg-[#40E0D0] hover:bg-[#36c4b6] text-slate-900"
              data-testid="telegram-save-btn"
            >
              {telegramSaving ? 'Speichert…' : 'Speichern'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const StatCard = ({ label, value, icon: Icon, accent, muted }) => (
  <div className={`bg-white rounded-2xl border border-slate-100 p-4 flex items-center gap-3 ${accent ? 'ring-2 ring-[#40E0D0]/30' : ''}`}>
    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${accent ? 'bg-[#40E0D0]/20 text-[#0a8377]' : muted ? 'bg-slate-100 text-slate-400' : 'bg-slate-50 text-slate-500'}`}>
      <Icon className="w-5 h-5" />
    </div>
    <div>
      <div className="text-2xl font-bold text-slate-900">{value}</div>
      <div className="text-xs text-slate-500">{label}</div>
    </div>
  </div>
);

const InfoBox = ({ icon: Icon, label, children }) => (
  <div className="bg-slate-50 rounded-xl p-3">
    <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
      <Icon className="w-3.5 h-3.5" />
      {label}
    </div>
    <div className="text-sm text-slate-900">{children}</div>
  </div>
);

const BreakdownCard = ({ title, icon: Icon, items, keyName, total }) => {
  if (!items || items.length === 0) return null;
  const max = Math.max(...items.map((i) => i.count), 1);
  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-5" data-testid={`breakdown-${title}`}>
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 bg-[#E0F7FA] rounded-lg flex items-center justify-center">
          <Icon className="w-4 h-4 text-[#40E0D0]" />
        </div>
        <h3 className="font-semibold text-slate-900">{title}</h3>
        <span className="ml-auto text-xs text-slate-400">{total} bestätigt</span>
      </div>
      <div className="space-y-3">
        {items.slice(0, 8).map((item) => {
          const label = item[keyName];
          const pct = total > 0 ? Math.round((item.count / total) * 100) : 0;
          return (
            <div key={label}>
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="text-slate-700 truncate pr-2">{label}</span>
                <span className="text-slate-500 text-xs shrink-0">
                  <strong className="text-slate-900">{item.count}</strong> · {pct}%
                </span>
              </div>
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#40E0D0] rounded-full transition-all"
                  style={{ width: `${(item.count / max) * 100}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AdminDashboard;
